/** @file Grid lattice maths: square tiles or iso diamonds as integer (u, v) cells (spec 5.10). */
import { trig } from '@pose2d/math/trig';
import type { GridSpec } from '../overlayOptions';

const p: [number, number] = [0, 0];

/**
 * Square: u = x / tileW, v = y / tileH. Iso: u = x / tileW + y / tileH, v = x / tileW - y / tileH, so the diamond
 * with corners (0, 0), (tileW / 2, tileH / 2), (tileW, 0), (tileW / 2, -tileH / 2) is the cell u, v in [0, 1].
 */
export const gridLattice = {
  /** World point (skeleton space, y up) of lattice coordinates (u, v). */
  toWorld(grid: GridSpec, u: number, v: number, out: [number, number]): [number, number] {
    if (grid.mode === 'square') {
      out[0] = u * grid.tileW;
      out[1] = v * grid.tileH;
    } else {
      out[0] = (grid.tileW * (u + v)) / 2;
      out[1] = (grid.tileH * (u - v)) / 2;
    }
    return out;
  },
  /** Lattice coordinates of a world point. */
  fromWorld(grid: GridSpec, x: number, y: number, out: [number, number]): [number, number] {
    const a = x / grid.tileW, b = y / grid.tileH;
    out[0] = grid.mode === 'square' ? a : a + b;
    out[1] = grid.mode === 'square' ? b : a - b;
    return out;
  },
  /** Lattice bounding range [uMin, uMax, vMin, vMax] of the world box [minX, minY, maxX, maxY]. */
  range(grid: GridSpec, box: readonly [number, number, number, number]): [number, number, number, number] {
    const r: [number, number, number, number] = [Infinity, -Infinity, Infinity, -Infinity];
    for (let i = 0; i < 4; i++) {
      // corners (minX, minY), (maxX, minY), (maxX, maxY), (minX, maxY)
      gridLattice.fromWorld(grid, i === 1 || i === 2 ? box[2] : box[0], i >= 2 ? box[3] : box[1], p);
      r[0] = Math.min(r[0], p[0]);
      r[1] = Math.max(r[1], p[0]);
      r[2] = Math.min(r[2], p[1]);
      r[3] = Math.max(r[3], p[1]);
    }
    return r;
  },
  /** World distance between neighbouring lines of constant u, and of constant v. */
  spacing(grid: GridSpec): [number, number] {
    if (grid.mode === 'square') return [grid.tileW, grid.tileH];
    const d = 1 / trig.hypot(1 / grid.tileW, 1 / grid.tileH);
    return [d, d];
  },
};
